import { useQuery } from '@tanstack/react-query';
import { CancelablePromise } from 'api-clients/monolith';
import { useEffect, useState } from 'react';

export type QueryParams = {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  query?: string;
  isSelectedForSourcing?: boolean;
};

type FetchFn<T, P> = (params: P) => CancelablePromise<T>;

type UseFilterQueryProps<T, P> = {
  fetchFn: FetchFn<T, P>;
  defaultParams?: Partial<P>;
  key: string;
};

export function useFilterQuery<T, P>({
  fetchFn,
  defaultParams = {},
  key,
}: UseFilterQueryProps<T, P>) {
  const [query, setQuery] = useState<string>();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce query input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: [key, debouncedQuery, defaultParams],
    queryFn: () => fetchFn({ ...defaultParams, query: debouncedQuery } as P),
    // Filter options (positions, locations, brands) change rarely – avoid refetch on every mount
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data,
    isFetching,
    error,
    query,
    setQuery,
    refetch,
  };
}
